import { useState, useEffect, useCallback } from 'react';
import { collection, doc, onSnapshot, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../utils/init-firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../utils/init-firebase';

/**
 * Hook to manage appSettings documents in Firestore.
 * Provides real-time subscription, update helpers, and deploy trigger.
 */
export function useAppSettings() {
  const [appSettings, setAppSettings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Real-time subscription to appSettings collection
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'appSettings'), (snapshot) => {
      const apps = [];
      snapshot.forEach((doc) => {
        apps.push({ id: doc.id, ...doc.data() });
      });
      setAppSettings(apps);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Update or create an app setting
  const updateAppSetting = useCallback(async (appId, data) => {
    const docRef = doc(db, 'appSettings', appId);
    await setDoc(docRef, { ...data, updatedAt: serverTimestamp() }, { merge: true });
  }, []);

  // Toggle a user's access to an app
  const toggleUserAccess = useCallback(async (appId, uid, currentAllowedUsers = []) => {
    const docRef = doc(db, 'appSettings', appId);
    const isCurrentlyAllowed = currentAllowedUsers.includes(uid);
    const newAllowedUsers = isCurrentlyAllowed
      ? currentAllowedUsers.filter(u => u !== uid)
      : [...currentAllowedUsers, uid];

    await updateDoc(docRef, { allowedUsers: newAllowedUsers });
    return !isCurrentlyAllowed; // returns new state
  }, []);

  // Toggle allowAll for an app
  const toggleAllowAll = useCallback(async (appId, currentValue) => {
    const docRef = doc(db, 'appSettings', appId);
    await updateDoc(docRef, { allowAll: !currentValue });
  }, []);

  // Trigger deployment via Cloud Function
  const triggerDeploy = useCallback(async (appId) => {
    const functionsObj = getFunctions(app, 'us-east1');
    const triggerAppDeploy = httpsCallable(functionsObj, 'triggerAppDeploy');
    const result = await triggerAppDeploy({ appId });
    return result.data;
  }, []);

  // Fetch deployment history via Cloud Function (Firebase Hosting releases)
  const fetchDeployHistory = useCallback(async (appId) => {
    const functionsObj = getFunctions(app, 'us-east1');
    const getAppDeployments = httpsCallable(functionsObj, 'getAppDeployments');
    const result = await getAppDeployments({ appId });
    return result.data.releases;
  }, []);

  // Rollback to a previous Firebase Hosting version
  const rollbackDeploy = useCallback(async (appId, versionName) => {
    const functionsObj = getFunctions(app, 'us-east1');
    const rollbackAppDeploy = httpsCallable(functionsObj, 'rollbackAppDeploy');
    const result = await rollbackAppDeploy({ appId, versionName });
    return result.data;
  }, []);

  // Initialize default app settings (call once to seed Firestore)
  const initializeApp = useCallback(async (appId, config) => {
    const docRef = doc(db, 'appSettings', appId);
    await setDoc(docRef, {
      appId,
      appName: config.appName || appId,
      allowAll: false,
      allowedUsers: [],
      repoOwner: config.repoOwner || '',
      repoName: config.repoName || '',
      workflowFile: config.workflowFile || 'deploy.yml',
      deployBranch: config.deployBranch || 'prod',
      firebaseSiteId: config.firebaseSiteId || '',
      appUrl: config.appUrl || '',
      lastDeployedAt: null,
      lastDeployedBy: null,
      createdAt: serverTimestamp(),
    }, { merge: true });
  }, []);

  return {
    appSettings,
    loading,
    updateAppSetting,
    toggleUserAccess,
    toggleAllowAll,
    triggerDeploy,
    fetchDeployHistory,
    rollbackDeploy,
    initializeApp,
  };
}
